import { Box } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { memo, type FC, type UIEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const SIZE = {
    compact: {
        visibleRows: 3,
        itemH: 28,
        fontSize: 13,
        selectedFontSize: 16,
        minWidth: 36,
        borderRadius: '8px',
        selectionBorderWidth: 2,
        selectionMinWidth: 56,
        selectionMinHeight: 28,
        selectionInsetX: 4,
        itemPx: 1.25,
        grow: false,
    },
    md: {
        visibleRows: 5,
        itemH: 40,
        fontSize: 16,
        selectedFontSize: 16,
        minWidth: 52,
        borderRadius: '10px',
        selectionBorderWidth: 2,
        selectionMinWidth: 52,
        selectionMinHeight: 0,
        selectionInsetX: 4,
        itemPx: 1.5,
        grow: true,
    },
    record: {
        visibleRows: 3,
        itemH: 48,
        fontSize: 15,
        selectedFontSize: 24,
        minWidth: 52,
        borderRadius: '12px',
        selectionBorderWidth: 2,
        selectionMinWidth: 52,
        selectionMinHeight: 48,
        selectionInsetX: 4,
        itemPx: 1.5,
        grow: true,
    },
    lg: {
        visibleRows: 5,
        itemH: 64,
        fontSize: 24,
        selectedFontSize: 40,
        minWidth: 56,
        borderRadius: '16px',
        selectionBorderWidth: 3,
        selectionMinWidth: 108,
        selectionMinHeight: 58,
        selectionInsetX: 4,
        itemPx: 1.5,
        grow: true,
    },
    xl: {
        visibleRows: 5,
        itemH: 72,
        fontSize: 28,
        selectedFontSize: 46,
        minWidth: 64,
        borderRadius: '16px',
        selectionBorderWidth: 3,
        selectionMinWidth: 152,
        selectionMinHeight: 64,
        selectionInsetX: 4,
        itemPx: 1.5,
        grow: true,
    },
} as const;

const OVERSCAN_ROWS = 12;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

const smoothstep = (t: number) => {
    const x = clamp01(t);
    return x * x * (3 - 2 * x);
};

const itemFocus = (index: number, centerIdx: number) => smoothstep(1 - Math.abs(index - centerIdx));

export type WheelColumnProps = {
    count: number;
    selectedIndex: number;
    labelAt: (i: number) => string;
    onCommittedIndex: (i: number) => void;
    selectionOutline: string;
    size?: keyof typeof SIZE;
    visibleRows?: number;
};

export const WheelColumn: FC<WheelColumnProps> = memo(
    ({ count, selectedIndex, labelAt, onCommittedIndex, selectionOutline, size = 'md', visibleRows: visibleRowsProp }) => {
        const {
            visibleRows: sizeVisibleRows,
            itemH,
            fontSize,
            selectedFontSize,
            minWidth,
            borderRadius,
            selectionBorderWidth,
            selectionMinWidth,
            selectionMinHeight,
            selectionInsetX,
            itemPx,
            grow,
        } = SIZE[size];
        const visibleRows = visibleRowsProp ?? sizeVisibleRows;
        const wheelH = itemH * visibleRows;
        const pad = (wheelH - itemH) / 2;
        const ref = useRef<HTMLDivElement>(null);
        const scrollEndT = useRef<ReturnType<typeof setTimeout> | null>(null);
        const rafT = useRef<number | null>(null);
        const lastCommitted = useRef(selectedIndex);
        const [centerIdx, setCenterIdx] = useState(selectedIndex);

        const snap = useCallback(() => {
            const el = ref.current;
            if (!el || count <= 0) return;
            const idx = Math.min(count - 1, Math.max(0, Math.round(el.scrollTop / itemH)));
            setCenterIdx(idx);
            if (idx !== lastCommitted.current) {
                lastCommitted.current = idx;
                onCommittedIndex(idx);
            }
            const target = idx * itemH;
            if (Math.abs(el.scrollTop - target) > 1) {
                el.scrollTop = target;
            }
        }, [count, itemH, onCommittedIndex]);

        const goToIndex = useCallback(
            (i: number) => {
                const el = ref.current;
                if (!el || count <= 0) return;
                const idx = Math.min(count - 1, Math.max(0, i));
                lastCommitted.current = idx;
                setCenterIdx(idx);
                el.scrollTop = idx * itemH;
                onCommittedIndex(idx);
            },
            [count, itemH, onCommittedIndex],
        );

        useLayoutEffect(() => {
            const el = ref.current;
            if (!el || count <= 0) return;
            const idx = Math.min(count - 1, Math.max(0, selectedIndex));
            lastCommitted.current = idx;
            setCenterIdx(idx);
            el.scrollTop = idx * itemH;
        }, [count, itemH, selectedIndex]);

        const onScroll = (_e: UIEvent<HTMLDivElement>) => {
            const el = ref.current;
            if (el && count > 0) {
                if (rafT.current != null) cancelAnimationFrame(rafT.current);
                rafT.current = requestAnimationFrame(() => {
                    rafT.current = null;
                    const idx = Math.min(count - 1, Math.max(0, Math.round(el.scrollTop / itemH)));
                    setCenterIdx(prev => (prev === idx ? prev : idx));
                });
            }
            if (scrollEndT.current) clearTimeout(scrollEndT.current);
            scrollEndT.current = setTimeout(() => {
                scrollEndT.current = null;
                snap();
            }, 100);
        };

        useEffect(
            () => () => {
                if (scrollEndT.current) clearTimeout(scrollEndT.current);
                if (rafT.current != null) cancelAnimationFrame(rafT.current);
            },
            [],
        );

        const windowStart = Math.max(0, centerIdx - OVERSCAN_ROWS);
        const windowEnd = Math.min(count, centerIdx + OVERSCAN_ROWS + 1);
        const items = useMemo(() => {
            const rows: number[] = [];
            for (let i = windowStart; i < windowEnd; i++) rows.push(i);
            return rows;
        }, [windowEnd, windowStart]);

        return (
            <Box
                sx={{
                    position: 'relative',
                    height: wheelH,
                    flex: grow ? 1 : '0 0 auto',
                    minWidth: grow ? 0 : selectionMinWidth,
                    width: grow ? '100%' : undefined,
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: pad + (itemH - (selectionMinHeight > 0 ? selectionMinHeight : itemH)) / 2,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: `calc(100% - ${selectionInsetX * 2}px)`,
                        height: selectionMinHeight > 0 ? selectionMinHeight : itemH,
                        borderRadius,
                        border: `${selectionBorderWidth}px solid ${selectionOutline}`,
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        zIndex: 2,
                    }}
                />
                <Box
                    ref={ref}
                    role="listbox"
                    onScroll={onScroll}
                    onPointerUp={snap}
                    sx={{
                        height: wheelH,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        scrollSnapType: 'y mandatory',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    <Box sx={{ paddingTop: `${pad}px`, paddingBottom: `${pad}px` }}>
                        {windowStart > 0 ? <Box sx={{ height: windowStart * itemH }} aria-hidden /> : null}
                        {items.map(i => {
                            const focus = itemFocus(i, centerIdx);
                            const rowFontSize = fontSize + (selectedFontSize - fontSize) * focus;
                            const opacity = 0.42 + 0.58 * focus;
                            return (
                                <Box
                                    key={i}
                                    role="option"
                                    aria-selected={i === selectedIndex}
                                    onPointerUp={e => e.stopPropagation()}
                                    onClick={() => goToIndex(i)}
                                    sx={{
                                        height: itemH,
                                        scrollSnapAlign: 'center',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            px: itemPx,
                                            minWidth,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Text
                                            style={{
                                                fontSize: rowFontSize,
                                                fontWeight: focus > 0.55 ? 800 : 500,
                                                color: 'var(--tg-theme-text-color)',
                                                opacity,
                                                lineHeight: 1,
                                            }}
                                        >
                                            {labelAt(i)}
                                        </Text>
                                    </Box>
                                </Box>
                            );
                        })}
                        {windowEnd < count ? <Box sx={{ height: (count - windowEnd) * itemH }} aria-hidden /> : null}
                    </Box>
                </Box>
            </Box>
        );
    },
);

WheelColumn.displayName = 'WheelColumn';
